import type { Theme } from '@mui/material'
import Avatar from '@mui/material/Avatar'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { TripDetailsContext } from 'context/trips/TripDetailsContext'
import { type ReactNode, useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import LocationLink from 'screens/TripDashboard/LocationLink'
import { getStopsGroupStateColor, getTypeIcon, getTypeLabel } from 'screens/TripDetails/TripDetails.helpers'
import type { StopsSequenceGroup } from 'screens/TripDetails/TripDetails.types'

const PickupIcon = getTypeIcon('PUP')
const DeliveryIcon = getTypeIcon('DEL')
const CustomsIcon = getTypeIcon('CUS')

interface StopGroupHeaderProps {
  group: StopsSequenceGroup
}

const StopGroupHeader = ({
  group,
}: StopGroupHeaderProps): ReactNode => {
  const { t } = useTranslation()
  const [tripDetailsState, tripDetailsDispatch] = useContext(TripDetailsContext)

  const handleClick = useCallback(() => {
    tripDetailsDispatch({
      type: 'setActiveStopsGroup',
      payload: group.id,
    })
  }, [group])

  if (!group.stopLegPairs[0]) {
    return null
  }

  return (
    <Stack direction="column" spacing={0.5} onClick={handleClick}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Avatar
          sx={(theme: Theme) => ({
            backgroundColor: getStopsGroupStateColor(group.state),
            width: '1rem',
            height: '1rem',
            fontSize: theme.typography.size.sm,
            fontWeight: theme.typography.fontWeightSemiBold,
            color: '#fff',
          })}
        >
          {group.id}
        </Avatar>
        <Typography color="primary.main" variant="p2">
          {group.stopLegPairs[0].stop.address.name.join(' ') || getTypeLabel(group.stopLegPairs[0].stop.type)}
        </Typography>
      </Stack>
      <LocationLink
        countryCode={group.stopLegPairs[0].stop.address.countryCode}
        city={group.stopLegPairs[0].stop.address.city.join(', ')}
        zipCode={group.stopLegPairs[0].stop.address.zipCode}
        street={group.stopLegPairs[0].stop.address.street.join(', ')}
        marker={group.stopLegPairs[0].stop.geoPoint ? {
          id: group.stopLegPairs[0].stop.wayPointCid,
          latitude: group.stopLegPairs[0].stop.geoPoint.latitude,
          longitude: group.stopLegPairs[0].stop.geoPoint.longitude,
          type: group.stopLegPairs[0].stop.type,
        } : undefined}
        lineBreaks="all"
        hideMap={tripDetailsState.activeStopsGroup !== group.id}
      />
      <Stack spacing={2} direction="row" alignItems="center">
        {group.pickupsCount > 0 && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <PickupIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
            <Typography color="primary.main" variant="p5">
              {t('screens.cs.trip_details.card.pickups_count', { count: group.pickupsCount })}
            </Typography>
          </Stack>
        )}
        {group.deliveriesCount > 0 && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <DeliveryIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
            <Typography color="primary.main" variant="p5">
              {t('screens.cs.trip_details.card.deliveries_count', { count: group.deliveriesCount })}
            </Typography>
          </Stack>
        )}
        {group.customsCount > 0 && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CustomsIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
            <Typography color="primary.main" variant="p5">
              {t('screens.cs.trip_details.card.customs_count', { count: group.customsCount })}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

export default StopGroupHeader
